import CheckIcon from '@mui/icons-material/Check';
import ReportIcon from '@mui/icons-material/Report';
import SvgIcon from '@mui/material/SvgIcon';
import { useCallback, useMemo, useRef, useState } from 'react';

import { AnnotationType, Piece, TrackingNumberValidationStatus } from '@apiContract';
import { ValidationData } from '@components/MailpieceActions/types';

import { useGetPieceAnnotationFilteredQuery } from '@redux/api/pieceAnnotation';

import { ScanHighlightColor } from '../styles';
import { useScanLeaveReset } from './useScanLeaveReset';

export enum ScanState {
  Validated = 'validated',
  Duplicate = 'duplicate',
  NotIncluded = 'not-included',
  ImproperFormat = 'improper-format',
  NotUnique = 'not-unique',
}

export type DiscreteScanResult = {
  state: ScanState;
  code: string;
  piece?: Piece;
};

// The label QR code either matches a piece or it does not — the tracking number states cannot
// come out of the camera flow, so its chip has no copy for them.
type LabelScanState = ScanState.Validated | ScanState.Duplicate | ScanState.NotIncluded;

type TrackingNumberMatch = { pieceId: number; validationStatus?: TrackingNumberValidationStatus };

export type ScanFeedback = { color: ScanHighlightColor; Icon: typeof SvgIcon; label: string };

const FEEDBACK_BY_STATE: Record<LabelScanState, ScanFeedback> = {
  [ScanState.Validated]: { color: 'success', Icon: CheckIcon, label: 'Item validated' },
  [ScanState.Duplicate]: { color: 'success', Icon: CheckIcon, label: 'Item already validated' },
  [ScanState.NotIncluded]: { color: 'error', Icon: ReportIcon, label: 'Item not included' },
};

export const normalize = (value: string) => value.trim().toUpperCase();

const ANNOTATIONS_PER_PAGE = 1000;

type UseQrScanFeedbackParams = {
  piecesForValidate: Piece[];
  isValidated: (pieceId: number) => boolean;
  addValidation: (pieceId: number, result: ValidationData[number]) => void;
  withTrackingNumbers?: boolean;
};

export const useQrScanFeedback = ({
  piecesForValidate,
  isValidated,
  addValidation,
  withTrackingNumbers = false,
}: UseQrScanFeedbackParams) => {
  const [scanState, setScanState] = useState<LabelScanState | null>(null);
  const [discreteScan, setDiscreteScan] = useState<DiscreteScanResult | null>(null);
  const lastScannedQRRef = useRef<string | null>(null);

  const { cancelLeaveTimer, scheduleLeaveReset } = useScanLeaveReset(() => {
    setScanState(null);
    lastScannedQRRef.current = null;
  });

  const pieceIds = useMemo(() => piecesForValidate.map((piece) => piece.id), [piecesForValidate]);

  const { data: annotations, isFetching: annotationsFetching } = useGetPieceAnnotationFilteredQuery(
    {
      PieceId: pieceIds,
      AnnotationType: AnnotationType.TrackingNumber,
      ItemsPerPage: ANNOTATIONS_PER_PAGE,
    },
    { skip: !withTrackingNumbers || !pieceIds.length },
  );

  // A number listed for several pieces keeps every match, which is what makes conflicts detectable.
  // Every piece carrying the scanned number, which is what makes conflicts detectable.
  const getMatches = useCallback(
    (code: string) => {
      const scanned = normalize(code);

      return (annotations?.items || []).reduce<TrackingNumberMatch[]>(
        (matches, { textValue, pieceId, pieceAnnotationsTrackingNumber }) => {
          if (!textValue || normalize(textValue) !== scanned) return matches;

          matches.push({ pieceId, validationStatus: pieceAnnotationsTrackingNumber?.validationStatus });

          return matches;
        },
        [],
      );
    },
    [annotations],
  );

  // A piece can carry the same number on more than one annotation, so repeats are skipped.
  const getTrackingNumbers = useCallback(
    (id: number) =>
      (annotations?.items || []).reduce<string[]>((numbers, { textValue, pieceId }) => {
        if (!textValue || pieceId !== id || numbers.includes(textValue)) return numbers;

        numbers.push(textValue);

        return numbers;
      }, []),
    [annotations],
  );

  const validate = useCallback(
    (pieceId: number): { state: LabelScanState; piece?: Piece } => {
      const piece = piecesForValidate.find((p) => p.id === pieceId);
      if (!piece) return { state: ScanState.NotIncluded };
      if (isValidated(pieceId)) return { state: ScanState.Duplicate, piece };

      addValidation(pieceId, { validatedByQrCode: true });
      return { state: ScanState.Validated, piece };
    },
    [piecesForValidate, isValidated, addValidation],
  );

  // The camera decodes the QR code printed on the label, which encodes the piece id as `RD<id>`.
  const processLabelCode = useCallback(
    (code: string): { state: LabelScanState; piece?: Piece } => {
      const labelMatch = /^RD(\d+)$/i.exec(code);
      if (!labelMatch) return { state: ScanState.NotIncluded };

      return validate(Number(labelMatch[1]));
    },
    [validate],
  );

  // A hardware scanner reads the carrier barcode, so the code is matched against the pieces'
  // tracking number annotations.
  const processTrackingNumber = useCallback(
    (code: string): { state: ScanState; piece?: Piece } => {
      const matches = getMatches(code);
      if (!matches.length) return { state: ScanState.NotIncluded };

      if (matches.some(({ validationStatus }) => validationStatus === TrackingNumberValidationStatus.BadFormat)) {
        return { state: ScanState.ImproperFormat };
      }

      const matchedPieceIds = matches.map(({ pieceId }) => pieceId);
      const isConflict =
        matchedPieceIds.length > 1 ||
        matches.some(({ validationStatus }) => validationStatus === TrackingNumberValidationStatus.Conflict);
      if (isConflict) return { state: ScanState.NotUnique };

      return validate(matches[0].pieceId);
    },
    [getMatches, validate],
  );

  const handleScannedData = useCallback(
    (qrData: string) => {
      cancelLeaveTimer();

      if (lastScannedQRRef.current === qrData) return;
      lastScannedQRRef.current = qrData;

      setScanState(processLabelCode(qrData.trim()).state);
    },
    [processLabelCode, cancelLeaveTimer],
  );

  const handleDiscreteScan = useCallback(
    (data: string) => {
      const code = data.trim();
      setDiscreteScan({ code, ...processTrackingNumber(code) });
    },
    [processTrackingNumber],
  );

  return {
    feedbackChip: scanState ? FEEDBACK_BY_STATE[scanState] : null,
    discreteScan,
    getTrackingNumbers,
    // Scans must be rejected until the lookup is ready, otherwise a valid code reads as not included.
    annotationsLoading: withTrackingNumbers && !!pieceIds.length && annotationsFetching,
    handleScannedData,
    handleDiscreteScan,
    scheduleLeaveReset,
  };
};
